import {
  CameraIntegration,
  getContactSupportMessage,
  epochTimeOfSelectedEvent,
} from './base';
import { post } from '../securityService';
import { i18n } from '@qsrsoft/myq-core';

const messages = {
  en: {
    wachter_submit_creds_failed: 'We were unable to submit your login information to Wachter at this time. Please try again later. If this error is reoccurring, please contact support for assistance. ',
    wachter_no_thumbnails: 'We were unable to find the camera thumbnails associated with your Wachter account. {contactSupportMessage}',
    wachter_no_auth_token: 'We were unable to authenticate your Wachter account. {contactSupportMessage}',
    wachter_no_orchid_id: 'We were unable to find the cameras associated with your Wachter account. {contactSupportMessage}',
    wachter_camera_not_found: 'We were unable to find the video for this camera because this camera was not found to be associated with your Wachter account. {contactSupportMessage}',
    wachter_camera_id_not_found: 'We were unable to identify the selected camera in order to display the associated video. Please contact support for assistance.',
  },
  es: {
    wachter_submit_creds_failed: 'No pudimos enviar su información de inicio de sesión a Wachter en este momento. Por favor, inténtelo de nuevo más tarde. Si este error persiste, comuníquese con soporte para obtener ayuda. ',
    wachter_no_thumbnails: 'No pudimos encontrar las miniaturas de cámara asociadas con su cuenta de Wachter. {contactSupportMessage}',
    wachter_no_auth_token: 'No pudimos autenticar su cuenta de Wachter. {contactSupportMessage}',
    wachter_no_orchid_id: 'No pudimos encontrar las cámaras asociadas con su cuenta de Wachter. {contactSupportMessage}',
    wachter_camera_not_found: 'No pudimos encontrar el video de esta cámara porque no se encontró que estuviera asociada con su cuenta de Wachter. {contactSupportMessage}',
    wachter_camera_id_not_found: 'No pudimos identificar la cámara seleccionada para mostrar el video asociado. Comuníquese con soporte para obtener ayuda.',
  },
  fr: {
    wachter_submit_creds_failed: "Nous n'avons pas pu soumettre vos informations de connexion à Wachter pour le moment. Veuillez réessayer plus tard. Si cette erreur persiste, veuillez contacter le support pour obtenir de l'aide. ",
    wachter_no_thumbnails: "Nous n'avons pas pu trouver les vignettes de caméra associées à votre compte Wachter. {contactSupportMessage}",
    wachter_no_auth_token: "Nous n'avons pas pu authentifier votre compte Wachter. {contactSupportMessage}",
    wachter_no_orchid_id: "Nous n'avons pas pu trouver les caméras associées à votre compte Wachter. {contactSupportMessage}",
    wachter_camera_not_found: "Nous n'avons pas pu trouver la vidéo de cette caméra car elle n'a pas été trouvée associée à votre compte Wachter. {contactSupportMessage}",
    wachter_camera_id_not_found: "Nous n'avons pas pu identifier la caméra sélectionnée pour afficher la vidéo associée. Veuillez contacter le support pour obtenir de l'aide.",
  },
  'fr-ca': {
    wachter_submit_creds_failed: "Nous n'avons pas pu soumettre vos informations de connexion à Wachter pour le moment. Veuillez réessayer plus tard. Si cette erreur persiste, veuillez contacter le support pour obtenir de l'aide. ",
    wachter_no_thumbnails: "Nous n'avons pas pu trouver les vignettes de caméra associées à votre compte Wachter. {contactSupportMessage}",
    wachter_no_auth_token: "Nous n'avons pas pu authentifier votre compte Wachter. {contactSupportMessage}",
    wachter_no_orchid_id: "Nous n'avons pas pu trouver les caméras associées à votre compte Wachter. {contactSupportMessage}",
    wachter_camera_not_found: "Nous n'avons pas pu trouver la vidéo de cette caméra car elle n'a pas été trouvée associée à votre compte Wachter. {contactSupportMessage}",
    wachter_camera_id_not_found: "Nous n'avons pas pu identifier la caméra sélectionnée pour afficher la vidéo associée. Veuillez contacter le support pour obtenir de l'aide.",
  },
};

Object.keys(messages).forEach((locale) => {
  i18n.global.mergeLocaleMessage(locale, messages[locale]);
});

export class Wachter extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'Wachter';
  }

  checkForCredentials() {
    // authToken = accessToken
    const accessToken = this.camera?.authToken;
    // if there is no camera list, we force the user to re log in
    this.isAuthenticated =
      !!accessToken &&
      accessToken !== 'refresh_token_invalid' &&
      accessToken !== 'refresh_token_not_available' &&
      this.camera.cameraList?.length > 0;
    this.camera.accessToken = accessToken;
  }

  async submitCredentials(username, password) {
    const { location } = this.camera;

    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/wachter_submit_creds/${location}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(i18n.global.t('wachter_submit_creds_failed'));
    });

    this.camera.authToken = response.authToken;
    this.camera.cameraList = response.cameraList;
    this.isAuthenticated = true;
  }

  async fetchThumbnails() {
    const thumbnails = [];

    const {
      cameraList,
      config: { orchidId },
      authToken,
    } = this.camera;

    cameraList.forEach((camera) => {
      const url = `https://mcdonalds.ipconfigure.com/service/orchids/${orchidId}/cameras/${camera.id}/preview?jwt=${authToken}`;
      thumbnails.push({ id: camera.id, src: url, title: camera.name });
    });

    if (thumbnails.length === 0)
      throw Error(
        i18n.global.t('wachter_no_thumbnails', { contactSupportMessage: getContactSupportMessage() }),
      );
    return thumbnails;
  }

  async fetchVideo(cameraId) {
    const nowPlaying = {
      offsetSeconds: 0,
      displayTime: this.event.time,
    };

    const {
      timezone,
      authToken,
      config: { orchidId },
      cameraList,
    } = this.camera;

    if (!authToken) {
      console.error('Missing wachter authToken');
      throw Error(
        i18n.global.t('wachter_no_auth_token', { contactSupportMessage: getContactSupportMessage() }),
      );
    }
    if (!orchidId) {
      console.error('Missing wachter orchidId');
      throw Error(
        i18n.global.t('wachter_no_orchid_id', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    if (cameraId)
      nowPlaying.camera = cameraList.find((camera) => camera.id === cameraId);
    else [nowPlaying.camera] = cameraList;

    if (Object.keys(nowPlaying.camera).length === 0) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList },
      );
      throw Error(
        i18n.global.t('wachter_camera_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    nowPlaying.event = this.event;
    nowPlaying.description = `${nowPlaying.camera.name} from ${nowPlaying.displayTime}`;
    nowPlaying.cameraId = nowPlaying.camera.id;
    nowPlaying.authToken = authToken;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        { cameraId },
      );
      throw Error(i18n.global.t('wachter_camera_id_not_found'));
    }

    const epochMilliSeconds = epochTimeOfSelectedEvent({
      format: 'milli',
      time: nowPlaying.displayTime,
      timezone,
    });

    const params = {
      'single-player-orchid': orchidId,
      'single-player': nowPlaying.camera.streamId,
      'start-time': epochMilliSeconds,
      'disable-webrtc': 1,
    };
    const url = `https://mcdonalds.ipconfigure.com/?${new URLSearchParams(
      params,
    ).toString()}`;

    nowPlaying.video = {
      type: 'iframe',
      src: url,
      vendor: 'wachter',
    };

    return nowPlaying;
  }
}
